import { Controller } from '@hotwired/stimulus';
import { dispatchEvent, subscribeEvent, unsubscribeEvent } from '@shared/script/eventDispatcher';
import { Toggler } from '@site-module/toggler';
import { NotificationType, statusClassMap } from '@shared/script/enums/notification';
import { fetchData } from '@shared/script/fetchData';

type WaitingListResponse = {
    blnReload: boolean;
    strReloadUrl: string;
    html: string;
};

type SubmitEventPayload = Event & {
    params: {
        eventId: string;
    };
};

export default class extends Controller {
    static values = {
        endpoint: String
    };
    static targets = ['placeholder', 'notifications', 'form', 'dismissButton'];
    protected abortController: AbortController | null;
    protected signal: AbortSignal | null;
    protected toggler: Toggler | null;
    protected eventId: string | null;
    protected boundAddEvent: (data: { eventId: string | number }) => Promise<void>;

    declare dismissButtonTarget: HTMLButtonElement;
    declare endpointValue: string;
    declare placeholderTarget: HTMLElement;
    declare formTarget: HTMLFormElement;

    fetchFn: typeof fetchData = fetchData;

    isLoading(isLoading: bool): void {
        if (isLoading === true) {
            this.element.classList.add('waiting-list-modal--loading');
        } else {
            this.element.classList.remove('waiting-list-modal--loading');
        }
    }

    showNotification(type: string): void {
        const notificationClassName = (statusType: NotificationType): string => {
            return `waiting-list-modal--${statusClassMap[statusType]}`;
        };

        this.isLoading(false);
        this.element.classList.add(notificationClassName(type));
        dispatchEvent('notifications:show', type);
        this.dismissButtonTarget.disabled = false;
    }

    resetNotifications(): false {
        Object.values(statusClassMap).forEach((className) => {
            this.element.classList.remove(`waiting-list-modal--${className}`);
        });
        dispatchEvent('notifications:hideAll');
        this.dismissButtonTarget.disabled = true;
    }

    connect(): void {
        this.boundAddEvent = this.handleAddEvent.bind(this);
        subscribeEvent('waiting-list-button:addEvent', this.boundAddEvent);
    }

    disconnect(): void {
        unsubscribeEvent('waiting-list-button:addEvent', this.boundAddEvent);
    }

    initializeToggler(): void {
        this.toggler = new Toggler('wf_toggle_tiers', ['hideTiers', 'showTiers']);
    }

    async handleAddEvent({ eventId }: { eventId: string | number}) : Promise<void> {
        this.resetNotifications();

        this.isLoading(true);

        this.abortController = new AbortController();
        this.signal = this.abortController.signal;

        const payload = new FormData();
        payload.append('id', eventId);

        try {
            const result = await this.fetchFn<WaitingListResponse>(this.endpointValue,
                'POST',
                {
                    body: payload,
                    signal: this.signal,
                }
            );

            if (!result.ok) {
                throw new Error(`Error fetching data: ${result.error.message}`);
            }

            const data = result.data;

            if (data.blnReload && typeof data.strReloadUrl === 'string') {
                window.location.href = data.strReloadUrl;
                return;
            }

            this.clear();
            this.eventId = eventId;
            this.isLoading(false);
            this.placeholderTarget.innerHTML = data.html;
            this.initializeToggler();

        } catch {
            this.showNotification(NotificationType.Error);
        }
    }

    async submit(event: SubmitEventPayload): Promise<void> {
        this.isLoading(true);

        event.preventDefault();
        event.stopPropagation();

        const form = new FormData(this.formTarget);
        form.append('id', event.params.eventId);

        try {
            const result = await this.fetchFn(this.endpointValue,
                'POST',
                {
                    body: form,
                    signal: this.signal
                }
            );

            if (!result.ok) {
                throw new Error(`Error submitting form: ${result.error.message}`);
            }

            this.clear();
            this.showNotification(NotificationType.Success);
        } catch {
            this.clear();
            this.showNotification(NotificationType.Error);
        }

        this.eventId = null;
        this.toggler?.unset();
    }

    abort(): void {
        this.abortController && this.abortController.abort();
        this.resetNotifications();
    }

    clear(): void {
        this.toggler?.unset();
        const childElements = Array.from(this.placeholderTarget.childNodes);

        if (childElements.length === 0) {
            return;
        }

        childElements.forEach((element) => {
            element.remove();
        });
    }

    dismiss(): void {
        this.clear();
        this.resetNotifications();
    }
}
